import type { AlgoliaJobAdsRequest } from "@/modules/jobAds/client/utils/computeAlgoliaJobAdsRequest"

import { NIL as NIL_UUID } from "uuid"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { getAssociatedCompaniesIds } from "@/modules/companies/client/utils/getAssociatedCompaniesId"
import { useGetUserSavedSearches } from "@/modules/jobAds/client/hooks/useGetUserSavedSearches"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { useSeenJobAds } from "@/modules/jobAds/client/hooks/useSeenJobAds"
import { computeAlgoliaJobAdsRequest } from "@/modules/jobAds/client/utils/computeAlgoliaJobAdsRequest"
import { getAlgoliaIndexName } from "@/modules/jobAds/client/utils/getAlgoliaIndexName"
import { getJobAdsSearchParamsFromURLSearchParams } from "@/modules/jobAds/client/utils/getJobAdsSearchParamsFromURLSearchParams"
import { useLocale } from "@/modules/locales/useLocale"
import { useAlgoliaIndexNameFromUnleash } from "@/modules/search/useAlgoliaIndexNameFromUnleash"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"
import { useCurrentUserSearchConfigFetch } from "@/modules/user/client/hooks/useCurrentUserSearchConfigFetch"
import { useCurrentUserSearchCriteriaFetch } from "@/modules/user/client/hooks/useCurrentUserSearchCriteriaFetch"

export const useComputeAlgoliaJobAdsRequest = (): AlgoliaJobAdsRequest => {
  const locale = useLocale()
  const { dataCareerCenter: careerCenter } = useCareerCenterFetch()
  const { data: savedSearches } = useGetUserSavedSearches()
  const { seenJobAds } = useSeenJobAds()
  const { dataCurrentUser: currentUser } = useCurrentUserFetch()
  const { dataCurrentUserSearchConfig: currentSearchConfig } = useCurrentUserSearchConfigFetch()
  const { dataCurrentUserSearchCriteria: currentUserSearchCriteria } = useCurrentUserSearchCriteriaFetch()
  // If we are on a company page, it will be resolved, otherwise, we won't have data.
  const { dataCompany } = useCompanyFetch()

  const searchParams = useJobAdsSearchParams()
  const {
    isAbroadOnly,
    candidacyTypes,
    companyBusinessTypes,
    companySectors,
    contractDurations,
    contractTypes,
    exclusive,
    jobWithImpact,
    lat,
    lng,
    locales,
    location,
    page,
    positionCategories,
    radius,
    remoteTypes,
    startDates,
    studyLevels,
    tags,
    sort,
    userQuery,
    workExperiences,
  } = getJobAdsSearchParamsFromURLSearchParams(searchParams)

  const groupOffers = searchParams.getAll("groupOffers")

  const algoliaIndexNameFromUnleash = useAlgoliaIndexNameFromUnleash()

  const indexName = getAlgoliaIndexName({ algoliaIndexNameFromUnleash, locale, sort })

  const contractTypesSearchConfig = currentSearchConfig?.searchFilters.filter(filter => filter.name === "contract")[0]
  const studyLevelsSearchConfig = currentSearchConfig?.searchFilters.filter(filter => filter.name === "study_levels")[0]

  const excludedStudyLevels = currentSearchConfig?.negativeOptions.studyLevels || []

  const schoolId = careerCenter?.id || NIL_UUID
  const ccPositionCategoryIds = careerCenter?.careerCenterConf?.positionCategoryIds || []

  const companyIds = dataCompany?.id
    ? [dataCompany.id, ...getAssociatedCompaniesIds(!groupOffers.length, dataCompany)]
    : []

  const isCareerCenter = careerCenter?.id !== NIL_UUID

  return computeAlgoliaJobAdsRequest({
    candidacyTypes,
    ccPositionCategoryIds,
    companyBusinessTypes,
    companyIds,
    companySectors,
    contractDurations,
    contractTypes,
    contractTypesSearchConfig,
    currentUserSearchCriteria,
    curriculumIds: currentUser?.attributes.curriculumIds || [],
    excludedStudyLevels,
    exclusive,
    facultySchoolId: careerCenter?.mainCareerCenterId,
    indexName,
    isAbroadOnly,
    isLogged: currentUser?.isLogged || false,
    jobWithImpact,
    lat,
    lng,
    locales,
    location,
    page,
    positionCategories,
    remoteTypes,
    schoolId,
    startDates,
    studyLevels,
    studyLevelsSearchConfig,
    tags,
    userCountry: currentUser?.attributes.country,
    userQuery,
    workExperiences,
    ...(radius && { radius }),
    ...(isCareerCenter && { ccShortName: careerCenter?.shortName }),
    savedSearches,
    seenJobAds,
  })
}
